import { Injectable } from "@angular/core";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { environment } from "src/environments/environment";
import { UserService } from "../authentication/user.service";

@Injectable({
  providedIn: "root",
})
export class ApplicationInsightsService {
  appInsights: ApplicationInsights;
  connectionString: string;

  constructor(private userService: UserService) {
    const connectionString = environment.appInsights.connectionString;
    if (!connectionString) {
      // add custom behavior to handle this scenario
      console.error("Application Insights connection string is not found.");
      return;
    }

    this.appInsights = new ApplicationInsights({
      config: {
        connectionString: connectionString,
        enableAutoRouteTracking: true, // optional: auto-log all route changes
      },
    });
    this.appInsights.loadAppInsights();
  }

  // helper methods to track a variety of events and metric
  logPageView(name?: string, url?: string) {
    if (!this.appInsights) {
      return;
    }

    this.appInsights.trackPageView({
      name: name,
      uri: url,
    });
  }

  logEvent(name: string, properties?: { [key: string]: any }) {
    if (!this.appInsights) {
      return;
    }

    let userProperties = this.getUserProperties();
    properties = { ...userProperties, ...properties };
    this.appInsights.trackEvent({ name: name }, properties);
  }

  logMetric(
    name: string,
    average: number,
    properties?: { [key: string]: any }
  ) {
    if (!this.appInsights) {
      return;
    }
    let userProperties = this.getUserProperties();
    properties = { ...userProperties, ...properties };
    this.appInsights.trackMetric({ name: name, average: average }, properties);
  }

  logException(
    exception: Error,
    severityLevel?: number,
    properties?: { [key: string]: any }
  ) {
    if (!this.appInsights) {
      return;
    }

    let userProperties = this.getUserProperties();
    properties = { ...userProperties, ...properties };
    this.appInsights.trackException(
      {
        exception: exception,
        severityLevel: severityLevel,
      },
      properties
    );
  }

  logTrace(message: string, properties?: { [key: string]: any }) {
    if (!this.appInsights) {
      return;
    }
    let userProperties = this.getUserProperties();
    properties = { ...userProperties, ...properties };
    this.appInsights.trackTrace({ message: message }, properties);
  }

  private getUserProperties() {
    const user = this.userService.getUser();
    if (!user) {
      return {};
    }

    return {
      UserGuid: user.userGuid,
      UserName: user.username,
    };
  }
}
